<script setup>
// Lista "Oportunidades" da barra lateral da conversa.
//
// Tinha os MESMOS três defeitos que o card do quadro (corrigidos lá antes):
// locale cru no Intl, valor fantasma e enum indo pra tela. Passa a usar os
// mesmos helpers, pra não divergirem de novo.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatCurrency } from 'dashboard/helper/intlLocale';
import { dealValueCents } from './dealValue.js';

const props = defineProps({
  deal: {
    type: Object,
    required: true,
  },
});

defineEmits(['click']);

const { t, locale } = useI18n();

const formattedValue = computed(() => {
  // Presença vem de `value_cents`: o model deriva `value = value_cents / 100`,
  // então negócio sem valor chegava como `0.0` e esta lista mostrava um preço
  // que não existe em vez de "Sem valor".
  const cents = dealValueCents(props.deal);
  if (cents === null) return t('KANBAN.DEAL.NO_VALUE');
  // `formatCurrency` normaliza `pt_BR` -> `pt-BR`. O `Intl` cru ESTOURA com
  // underscore, e o try/catch que havia aqui engolia o RangeError e caía no
  // fallback: era por isso que a lista exibia `BRL 0.00`.
  return formatCurrency(cents / 100, props.deal.currency, locale.value);
});

// O enum do banco (`open`/`won`/`lost`) ia cru pro selo, e o `uppercase` do CSS
// o disfarçava de rótulo: "OPEN" numa tela em português.
const statusLabel = computed(() =>
  t(`KANBAN.DEAL.STATUS.${String(props.deal.status || 'open').toUpperCase()}`)
);

const statusDotClass = computed(() => {
  switch (props.deal.status) {
    case 'won':
      return 'bg-n-teal-9';
    case 'lost':
      return 'bg-n-ruby-9';
    default:
      return 'bg-n-amber-9';
  }
});

const statusBadgeClass = computed(() => {
  switch (props.deal.status) {
    case 'won':
      return 'bg-n-teal-3 text-n-teal-11';
    case 'lost':
      return 'bg-n-ruby-3 text-n-ruby-11';
    default:
      return 'bg-n-slate-3 text-n-slate-11';
  }
});
</script>

<template>
  <button
    type="button"
    class="w-full flex items-center gap-3 rounded-md border border-n-weak bg-white dark:bg-n-solid-2 p-2.5 text-left hover:border-n-slate-7 transition-colors"
    @click="$emit('click', deal)"
  >
    <span class="shrink-0 w-2.5 h-2.5 rounded-full" :class="statusDotClass" />
    <div class="flex-1 min-w-0">
      <p class="text-sm font-semibold text-n-slate-12 truncate">
        {{ deal.title }}
      </p>
      <p class="text-xs text-n-slate-11 mt-0.5">
        {{ formattedValue }}
      </p>
    </div>
    <span
      data-test="mini-deal-status"
      class="text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0"
      :class="statusBadgeClass"
    >
      {{ statusLabel }}
    </span>
  </button>
</template>
